import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { MoneyModel } from '~/models';
import { NotificationType } from '~/types/api/notification.model';
import styles from './AdHocInvoiceApproval.module.scss';
import { AlreadyPaidScreen } from './AlreadyPaidScreen';
import { ConfirmationScreen } from './ConfirmationScreen';
import { InvoiceSummaryPanel } from './InvoiceSummaryPanel';
import { PaymentMethodPanel } from './PaymentMethodPanel/PaymentMethodPanel';
export const GetAdHocInvoiceApprovalTID = (parentTestId)=>getTestIds(parentTestId);
/**
 * Ad-hoc invoice approval (client-facing payment) page.
 *
 * Data source assumption: the page receives the notification id via the `nid` query parameter,
 * appended by the email link / notification redirect. The URL path carries the invoiceId for
 * legibility but the canonical fetch is by notificationId because the no-auth endpoint resolves
 * the full payload (including auth token) from a single notification record.
 *
 * If `nid` is missing the page renders the error state — there is currently no fallback path,
 * since the no-auth endpoint requires a notification id.
 */ export const AdHocInvoiceApproval = (props)=>{
    var _state_payload_vendorId, _state_payload_customerId;
    const { testId } = props;
    const { AdHocInvoiceApprovalTestIds } = GetAdHocInvoiceApprovalTID(testId);
    const { t } = useLocaleKeys();
    const { api } = useStoreActions();
    const location = useLocation();
    const { isMobileDevice } = useDevice();
    const [state, setState] = useState({
        kind: 'loading'
    });
    useEffect(()=>{
        const searchParams = new URLSearchParams(location.search);
        const notificationId = searchParams.get('nid');
        if (notificationId == null) {
            setState({
                kind: 'error'
            });
            return;
        }
        let cancelled = false;
        async function fetchPayload(nid) {
            try {
                const payload = await api.notification.getWhileLoggedOut(nid);
                if (cancelled) return;
                if (payload.type !== NotificationType.Invoice) {
                    setState({
                        kind: 'error'
                    });
                    return;
                }
                if (payload.authToken == null) {
                    setState({
                        kind: 'alreadyPaid',
                        payload
                    });
                    return;
                }
                setState({
                    kind: 'ready',
                    payload,
                    authToken: payload.authToken
                });
            } catch (unused) {
                if (!cancelled) setState({
                    kind: 'error'
                });
            }
        }
        fetchPayload(notificationId);
        return ()=>{
            cancelled = true;
        };
    }, [
        api,
        location.search
    ]);
    if (state.kind === 'loading') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": AdHocInvoiceApprovalTestIds.LoadingSkeleton(),
                className: styles.skeleton
            })
        });
    }
    if (state.kind === 'error') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": AdHocInvoiceApprovalTestIds.ErrorState(),
                className: styles.errorState,
                children: t.adHocInvoiceApproval.errorState()
            })
        });
    }
    if (state.kind === 'confirmation') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(ConfirmationScreen, {
                testId: AdHocInvoiceApprovalTestIds.ConfirmationScreen(),
                amount: state.amount,
                vendorName: state.vendorName
            })
        });
    }
    if (state.kind === 'alreadyPaid') {
        const { invoice } = state.payload;
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.Root(),
            children: /*#__PURE__*/ _jsx(AlreadyPaidScreen, {
                testId: AdHocInvoiceApprovalTestIds.AlreadyPaidScreen(),
                invoiceNumber: invoice.invoiceFormattedNumber,
                amount: MoneyModel.fromDTO(invoice.amount),
                onDownloadPdf: ()=>{
                // Wired in a later chunk — triggers PDF download via API.
                }
            })
        });
    }
    const { invoice } = state.payload;
    const amount = MoneyModel.fromDTO(invoice.amount);
    const vendorName = invoice.vendorName;
    const onPaymentSubmitted = ()=>setState({
            kind: 'confirmation',
            amount,
            vendorName
        });
    const rootClassName = "".concat(styles.page, " ").concat(isMobileDevice ? styles.mobile : '').trim();
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": AdHocInvoiceApprovalTestIds.Root(),
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": AdHocInvoiceApprovalTestIds.SplitLayout(),
            className: rootClassName,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.leftPanel,
                    children: /*#__PURE__*/ _jsx(InvoiceSummaryPanel, {
                        testId: AdHocInvoiceApprovalTestIds.InvoiceSummaryPanel(),
                        vendorName: vendorName,
                        invoiceNumber: invoice.invoiceFormattedNumber,
                        amountDue: amount,
                        onViewInvoiceClick: ()=>{
                        // Wired in a later chunk — opens the invoice preview modal.
                        }
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.rightPanel,
                    children: [
                        /*#__PURE__*/ _jsx(PaymentMethodPanel, {
                            testId: AdHocInvoiceApprovalTestIds.PaymentMethodPanel(),
                            vendorId: (_state_payload_vendorId = state.payload.vendorId) !== null && _state_payload_vendorId !== void 0 ? _state_payload_vendorId : '',
                            customerId: (_state_payload_customerId = state.payload.customerId) !== null && _state_payload_customerId !== void 0 ? _state_payload_customerId : '',
                            relationshipId: invoice.relationshipId,
                            onPaymentMethodSelected: ()=>{
                            // Task 13 wires the full submit flow. For now, selecting a payment
                            // method is a no-op at the page level; tests drive the transition via
                            // the PaymentSubmittedTestTrigger below.
                            }
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "data-testid": AdHocInvoiceApprovalTestIds.PaymentSubmittedTestTrigger(),
                            onClick: onPaymentSubmitted,
                            style: {
                                display: 'none'
                            },
                            "aria-hidden": "true"
                        })
                    ]
                }),
                isMobileDevice && /*#__PURE__*/ _jsx("div", {
                    "data-testid": AdHocInvoiceApprovalTestIds.MobilePayNowSticky(),
                    className: styles.mobilePayNowSticky,
                    children: t.adHocInvoiceApproval.mobile.payNow()
                })
            ]
        })
    });
};
